// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { usePageTitle } from '@/components/app-shell/AppShell';
import { EmptyState } from '@/components/greenlight/EmptyState';
import { Meter } from '@/components/greenlight/Meter';
import { MetricSparkline } from '@/components/greenlight/MetricSparkline';
import { PageHeader } from '@/components/greenlight/PageHeader';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { ClusterCapacitySection } from '@/components/health/ClusterCapacitySection';
import { ControlPlaneServicesSection } from '@/components/health/ControlPlaneServicesSection';
import { CrashesSection } from '@/components/health/CrashesSection';
import {
  AppHealthCard,
  CloudResourcesCard,
  PostgresCard,
  VersionsCard,
} from '@/components/health/HealthCards';
import { HealthHero } from '@/components/health/HealthHero';
import { KubernetesClusterSection } from '@/components/health/KubernetesClusterSection';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import { useCurrentUser } from '@/lib/auth';
import { HEALTH_POLL_MS, POSTURE_KEY } from '@/lib/healthPoll';
import { AppReservationsSection } from '@/pages/internal-health/AppReservationsSection';
import { FleetReportingSection } from '@/pages/internal-health/FleetReportingSection';
import { QueueHealthSection } from '@/pages/internal-health/QueueHealthSection';
import { ReleaseValidationBanner } from '@/pages/internal-health/ReleaseValidationBanner';
import { ReleaseValidationSection } from '@/pages/internal-health/ReleaseValidationSection';
import { NodeCounts } from '@/pages/internal-health/NodeCounts';
import { UpdaterSection } from '@/pages/internal-health/UpdaterSection';
import { UpstreamStatusSection } from '@/pages/internal-health/UpstreamStatusSection';
import { EgressSection } from '@/pages/internal-health/EgressSection';
import {
  assessAppRuntimeCapacity,
  isCapacityPressure,
  type HealthFreshness,
  type HealthSnapshot,
} from '@greenlight/shared';

import { PERF_TARGETS } from '@/pages/internal-health/perfTargets';
import { CardHistory } from '@/pages/internal-health/history/CardHistory';
import {
  BackgroundWorkCharts,
  ClusterCharts,
  DatabaseCharts,
  RequestCharts,
  ServiceCharts,
} from '@/pages/internal-health/history/HealthHistoryCharts';

export { queueMessages } from '@/components/health/HealthCards';

// Latency is an SLO, not a utilization gauge: color against the route's target
// (docs/22 NFRs). Amber as it approaches the target (from 80%), red at/over it.
// domainMax gives a little headroom above target so an over-target spike still
// reads as taller red rather than clamping flat.
function latencyBands(target: number): { domainMax: number; warn: number; crit: number } {
  return { domainMax: Math.round(target * 1.3), warn: Math.round(target * 0.8), crit: target };
}

const numberFmt = new Intl.NumberFormat('en-US');

/** "45s ago", "6m ago", "2h ago" — the coarsest unit that still reads as a duration. */
function formatAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

/**
 * How old the newest observation is, counted from the server's reading rather
 * than the browser's clock. Past the freshness bound the green badge goes: a
 * collector that died leaves a green row behind it, and an endpoint that answers
 * instantly says nothing about when that row was written.
 */
function CollectionFreshness({
  freshness,
}: {
  readonly freshness: HealthFreshness;
}): React.ReactElement {
  const base = freshness.age_s ?? 0;
  const [elapsed, setElapsed] = React.useState(0);

  React.useEffect(() => {
    setElapsed(0);
    const timer = setInterval(() => setElapsed((seconds) => seconds + 1), 1000);
    return () => clearInterval(timer);
  }, [freshness.collected_at, freshness.age_s]);

  const age = base + elapsed;
  // The server's verdict decides the badge; the local tick only keeps the number
  // honest between polls, and crossing the bound locally says the same thing.
  const stale = freshness.stale || age > freshness.threshold_s;

  return (
    <span
      data-testid="health-freshness"
      className={`flex items-center gap-2 text-[12px] font-medium ${
        stale ? 'text-[hsl(var(--status-amber-text))]' : 'text-[hsl(var(--status-green-text))]'
      }`}
    >
      <span className="relative inline-flex h-2.5 w-2.5">
        {stale ? null : (
          <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--status-green))] opacity-50 motion-safe:animate-ping" />
        )}
        <span
          className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
            stale ? 'bg-[hsl(var(--status-amber))]' : 'bg-[hsl(var(--status-green))]'
          }`}
        />
      </span>
      {stale ? `Stale · collected ${formatAge(age)}` : `Live · ${formatAge(age)}`}
    </span>
  );
}

export type HealthTab = 'cluster' | 'updater' | 'app' | 'release-validation';

const HEALTH_TABS: readonly { key: HealthTab; label: string }[] = [
  { key: 'cluster', label: 'Cluster health' },
  { key: 'updater', label: 'Updater health' },
  { key: 'app', label: 'App health' },
  { key: 'release-validation', label: 'Release validation' },
];

/** Release validation is a tab only on an install that is validating releases. */
function visibleHealthTabs(
  releaseValidation: boolean,
): readonly { key: HealthTab; label: string }[] {
  return releaseValidation
    ? HEALTH_TABS
    : HEALTH_TABS.filter((t) => t.key !== 'release-validation');
}

export function parseHealthTab(value: string | null, releaseValidation = false): HealthTab {
  return visibleHealthTabs(releaseValidation).find((t) => t.key === value)?.key ?? 'cluster';
}

function HealthDataView({
  snapshot,
  freshness,
  fleetReporting,
  releaseValidationEnabled,
}: {
  readonly snapshot: HealthSnapshot;
  readonly freshness?: HealthFreshness;
  readonly fleetReporting: React.ReactNode;
  /** `undefined` while the release-validation status is still loading. */
  readonly releaseValidationEnabled: boolean | undefined;
}): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const showReleaseValidation = releaseValidationEnabled === true;
  const tabs = visibleHealthTabs(showReleaseValidation);
  const activeTab = parseHealthTab(tabParam, showReleaseValidation);
  // The same cache entry the Updater card reads; the hero only needs its one
  // verdict about the pod token.
  const postureQuery = api.health.updaterPosture.useQuery(POSTURE_KEY, undefined, {
    queryKey: POSTURE_KEY,
    refetchInterval: HEALTH_POLL_MS,
  });
  const credentialsRejected =
    postureQuery.data?.status === 200 &&
    postureQuery.data.body.cluster_credentials_rejected === true;
  const releaseValidationPending = releaseValidationEnabled === undefined;

  // An unknown ?tab= is rewritten to the tab actually shown; a bare URL is left alone.
  // A release-validation link waits for the status read before it is judged unknown.
  React.useEffect(() => {
    if (tabParam === 'release-validation' && releaseValidationPending) return;
    if (tabParam !== null && tabParam !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true, preventScrollReset: true });
    }
  }, [tabParam, activeTab, releaseValidationPending, searchParams, setSearchParams]);

  // On a narrow screen the tab bar scrolls sideways; keep the open tab in view.
  const tabListRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const list = tabListRef.current;
    const active = list?.querySelector<HTMLElement>(`[data-testid="health-tab-${activeTab}"]`);
    if (!list || !active) return;
    const bar = list.getBoundingClientRect();
    const tab = active.getBoundingClientRect();
    if (tab.left < bar.left) list.scrollLeft += tab.left - bar.left;
    else if (tab.right > bar.right) list.scrollLeft += tab.right - bar.right;
  }, [activeTab, tabs.length]);

  const selectTab = (value: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { preventScrollReset: true });
  };
  const pod = snapshot.pods.control_plane;
  const capacity = snapshot.app_runtime_capacity ?? null;
  const capacityAssessment = assessAppRuntimeCapacity(capacity, snapshot.cluster_nodes);
  const latencyMeasured = hasLatencyMeasurement(snapshot.hotpath_latency, snapshot.trends);

  const totalBillingMinutes = snapshot.billing.app_running_minutes.reduce(
    (sum, e) => sum + e.minutes,
    0,
  );

  type HotpathRow = {
    label: string;
    lat: { p50: number; p95: number; p99: number };
    target: number;
    trendKey: string;
  };

  const hotpathRows: HotpathRow[] = [
    {
      label: '/auth/check',
      lat: snapshot.hotpath_latency.auth_check_ms,
      target: PERF_TARGETS.authCheckMs,
      trendKey: 'auth_check_latency',
    },
    {
      label: 'data proxy overhead',
      lat: snapshot.hotpath_latency.data_proxy_ms,
      target: PERF_TARGETS.dataProxyMs,
      trendKey: 'data_proxy_latency',
    },
    {
      label: 'MCP tool call (read)',
      lat: snapshot.hotpath_latency.mcp_tool_ms,
      target: PERF_TARGETS.mcpToolMs,
      trendKey: 'mcp_tool_latency',
    },
  ];

  return (
    <div className="space-y-5 mt-6">
      <HealthHero
        snapshot={snapshot}
        {...(freshness ? { freshness } : {})}
        credentialsRejected={credentialsRejected}
      />

      <Tabs value={activeTab} onValueChange={selectTab} activationMode="manual">
        <div className="sticky top-0 z-10 -mx-4 bg-background/85 px-4 backdrop-blur-sm md:top-14 md:mx-[calc(-1*clamp(24px,4vw,48px))] md:px-[clamp(24px,4vw,48px)]">
          <TabsList
            ref={tabListRef}
            aria-label="Internal Health areas"
            className="flex h-auto w-full justify-start gap-2 overflow-x-auto rounded-none border-b border-border bg-transparent p-0 text-foreground [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {tabs.map((t) => (
              <TabsTrigger
                key={t.key}
                value={t.key}
                data-testid={`health-tab-${t.key}`}
                className="-mb-px h-12 shrink-0 rounded-none border-b-[3px] border-transparent px-3 text-[15px] font-medium text-foreground hover:border-border data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:shadow-none data-[state=active]:ring-0"
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="cluster" className="mt-5 space-y-5">
          {/* First-viewport capacity alert — IT sees the exact reason without kubectl.
          Only actionable pressure fires it; an unavailable read ('unknown') shows as no-data, not an alarm. */}
          {isCapacityPressure(capacityAssessment.level) && (
            <Alert variant={capacityAssessment.level === 'critical' ? 'destructive' : 'default'}>
              <AlertTitle>
                {capacityAssessment.level === 'critical'
                  ? 'Cluster capacity exhausted'
                  : 'Cluster capacity low'}
              </AlertTitle>
              <AlertDescription>
                <p>{capacityAssessment.reason}</p>
                <p className="mt-2 text-xs">
                  IT action: raise how far the cluster can grow, below, raise the cloud provider
                  quota, clear the cluster-autoscaler backoff, lower an app&rsquo;s declared compute
                  via PR, or pause selected apps with the kill switch. Greenlight never pauses an
                  app on its own to reclaim capacity.
                </p>
              </AlertDescription>
            </Alert>
          )}

          {/* Detail grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {/* The cluster everything runs on: its nodes, then Greenlight's own pods on them. */}
            <KubernetesClusterSection cloud={snapshot.installation.cloud_provider}>
              <ClusterCapacitySection
                nodes={snapshot.cluster_nodes}
                capacity={capacity}
                history={
                  <CardHistory testId="cluster-history">
                    <ClusterCharts />
                  </CardHistory>
                }
                nodeCounts={<NodeCounts />}
              />
              <ControlPlaneServicesSection
                pod={pod}
                services={snapshot.platform_services}
                historyFor={(service, { replicas, label }) => (
                  <CardHistory
                    testId={`service-history-${service}`}
                    service={service}
                    className="mt-2"
                    {...(label ? { label } : {})}
                  >
                    <ServiceCharts {...(replicas ? { replicas } : {})} />
                    {service === 'worker' ? <BackgroundWorkCharts /> : null}
                  </CardHistory>
                )}
              />
            </KubernetesClusterSection>
            <VersionsCard versions={snapshot.versions} />
            <PostgresCard
              postgres={snapshot.postgres}
              history={
                <CardHistory testId="postgres-history">
                  <DatabaseCharts />
                </CardHistory>
              }
            />
            <QueueHealthSection
              queue={snapshot.queue}
              trend={snapshot.trends?.['queue_depth'] ?? []}
            />
            {/* Billing */}
            <SectionCard title="Billing">
              <p className="text-2xl font-extrabold text-foreground" data-testid="billing-minutes">
                {numberFmt.format(totalBillingMinutes)} min{' '}
                <span className="text-sm font-normal text-muted-foreground">· this month</span>
              </p>
              <MetricSparkline
                data={snapshot.trends?.['billing_minutes'] ?? []}
                className="mt-2"
                ariaLabel="App running minutes trend"
              />
            </SectionCard>
            <CloudResourcesCard health={snapshot.provider_resource_health} />
            <UpstreamStatusSection upstream={snapshot.upstream_status} />
            <EgressSection />
            {/* Hot-path latency — full width. Hidden until the rollups hold a real
            measurement: the writer that fills them is a separate prerequisite,
            and a panel of 0 ms against a 300 ms target reads as "we are fast"
            rather than "nothing measured this". */}
            {latencyMeasured ? (
              <SectionCard title="Hot-path latency" className="md:col-span-2">
                <p className="text-xs text-muted-foreground mb-4">
                  Response time over a rolling window, against each route's target.
                </p>
                <div className="space-y-5">
                  {hotpathRows.map((row) => {
                    const trend = snapshot.trends?.[row.trendKey] ?? [];
                    const b = latencyBands(row.target);
                    return (
                      <div key={row.label} className="space-y-1.5">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-sm text-muted-foreground">{row.label}</span>
                          <span className="text-lg font-semibold tabular-nums text-foreground">
                            {row.lat.p99}{' '}
                            <span className="text-sm font-normal text-muted-foreground">ms</span>
                          </span>
                        </div>
                        {/* Graph + value bar are flush (no gap) so they read as one unit. */}
                        <div>
                          <MetricSparkline
                            data={trend}
                            domainMax={b.domainMax}
                            thresholds={{ warn: b.warn, crit: b.crit }}
                            referenceValue={row.target}
                          />
                          <Meter
                            value={row.lat.p99}
                            target={row.target}
                            warn={b.warn}
                            crit={b.crit}
                          />
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          typical {row.lat.p50} · busy {row.lat.p95} · target {row.target} ms
                        </p>
                      </div>
                    );
                  })}
                </div>
                <CardHistory testId="hotpath-history">
                  <RequestCharts />
                </CardHistory>
              </SectionCard>
            ) : null}
          </div>
          {fleetReporting}
        </TabsContent>

        <TabsContent value="updater" className="mt-5 space-y-5">
          <UpdaterSection snapshot={snapshot} />
        </TabsContent>

        <TabsContent value="app" className="mt-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
            <AppHealthCard
              countByStatus={snapshot.apps.count_by_status}
              className="md:col-span-2"
            />
            <CrashesSection snapshot={snapshot} />
            {/* Which apps hold reservations they do not use. */}
            <AppReservationsSection pollMs={HEALTH_POLL_MS} />
          </div>
        </TabsContent>

        {showReleaseValidation ? (
          <TabsContent value="release-validation" className="mt-5">
            <ReleaseValidationSection pollMs={HEALTH_POLL_MS} />
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
}

/**
 * Whether the hot-path rollups hold a real measurement.
 *
 * The collector reads `replica_latency_rollups`, but the Fastify writer that
 * fills them is a separate prerequisite, so every bucket reads `0` on an install
 * where nothing writes them. Zeros are indistinguishable from a genuinely idle
 * window, and a panel of `0 ms` against a `300 ms` target reads as "we are
 * fast" rather than "nothing measured this". The section is therefore hidden
 * until some bucket or some trend point is non-zero — no flag to flip when the
 * writer ships.
 */
export function hasLatencyMeasurement(
  latency: HealthSnapshot['hotpath_latency'],
  trends: HealthSnapshot['trends'],
): boolean {
  const buckets = Object.values(latency);
  if (buckets.some((b) => b.p50 > 0 || b.p95 > 0 || b.p99 > 0)) return true;
  const series = ['auth_check_latency', 'data_proxy_latency', 'mcp_tool_latency'];
  return series.some((key) => (trends?.[key] ?? []).some((point) => point > 0));
}

export function InternalHealthPage(): React.ReactElement {
  usePageTitle('Internal Health');
  const user = useCurrentUser();

  // Internal health is scoped to org admins (docs/28-it-dashboard-ui.md §Internal Health).
  if (user?.role !== 'org_admin') {
    return (
      <div className="space-y-6" data-testid="internal-health-page">
        <PageHeader eyebrow="Org admin" title="Internal Health" />
        <EmptyState
          title="Access restricted"
          description="Internal health is available to organization admins."
        />
      </div>
    );
  }

  return <InternalHealthPageAdminView />;
}

function InternalHealthPageAdminView(): React.ReactElement {
  const { data, isLoading, isError } = api.health.latestSnapshot.useQuery(
    ['health-snapshot'],
    undefined,
    {
      queryKey: ['health-snapshot'],
      refetchInterval: HEALTH_POLL_MS,
    },
  );

  const snapshot = data?.status === 200 ? data.body.snapshot : undefined;
  const freshness = data?.status === 200 ? data.body.freshness : undefined;

  const releaseValidation = api.health.releaseValidationStatus.useQuery(
    ['health-release-validation'],
    undefined,
    { queryKey: ['health-release-validation'], refetchInterval: HEALTH_POLL_MS },
  );
  // Unknown until the status read answers; a failed read means no tab.
  let releaseValidationEnabled: boolean | undefined;
  if (releaseValidation.data?.status === 200) {
    releaseValidationEnabled = releaseValidation.data.body.enabled;
  } else if (releaseValidation.data != null || releaseValidation.isError) {
    releaseValidationEnabled = false;
  }

  return (
    <div className="space-y-6" data-testid="internal-health-page">
      <PageHeader
        eyebrow="Org admin"
        title="Internal Health"
        actions={
          snapshot != null && freshness?.collected_at != null ? (
            <CollectionFreshness freshness={freshness} />
          ) : undefined
        }
      />

      <ReleaseValidationBanner pollMs={HEALTH_POLL_MS} />

      {isError ? (
        <Alert variant="destructive" data-testid="health-error">
          <AlertTitle>Couldn't load health snapshot</AlertTitle>
          <AlertDescription>
            Something went wrong fetching the health snapshot. Retry in a moment.
          </AlertDescription>
        </Alert>
      ) : isLoading ? (
        <div className="space-y-3 mt-6" data-testid="health-loading">
          <Skeleton className="h-48 w-full rounded-gl-lg" />
          <div className="grid gap-4 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-40 w-full rounded-gl-lg" />
            ))}
          </div>
        </div>
      ) : snapshot === null ? (
        <div data-testid="health-empty">
          <EmptyState
            title="No snapshot yet"
            description="The first health snapshot arrives shortly after install. Check back in a moment."
          />
        </div>
      ) : snapshot != null ? (
        <HealthDataView
          snapshot={snapshot}
          {...(freshness ? { freshness } : {})}
          fleetReporting={<FleetReportingSection pollMs={HEALTH_POLL_MS} />}
          releaseValidationEnabled={releaseValidationEnabled}
        />
      ) : (
        <Alert variant="destructive" data-testid="health-error">
          <AlertTitle>Couldn't load health snapshot</AlertTitle>
          <AlertDescription>
            Something went wrong fetching the health snapshot. Retry in a moment.
          </AlertDescription>
        </Alert>
      )}

      {/* Fleet reporting and release validation sit on tabs once a snapshot exists, and stay reachable before one does. */}
      {snapshot == null && !isLoading ? (
        <>
          <ReleaseValidationSection pollMs={HEALTH_POLL_MS} />
          <FleetReportingSection pollMs={HEALTH_POLL_MS} />
        </>
      ) : null}
    </div>
  );
}
